import React, { createContext, useState, useContext, useCallback, useEffect } from 'react';
import { normalizeSubscriptionStatus } from './utils/subscriptionStatus';
import { ApiError } from './lib/apiClient';
import { accountService, subscriptionService } from './services';

const UserContext = createContext();

const mergeSubscriptionDetails = (subscription, details) => {
    if (!details) {
        return subscription;
    }

    return {
        ...subscription,
        status: normalizeSubscriptionStatus(details.status ?? subscription.status),
        plan: details.plan ?? subscription.plan ?? null,
        isAnnual: typeof details.isAnnual === 'boolean' ? details.isAnnual : (subscription.isAnnual ?? false),
        entitlement: details.entitlement ?? subscription.entitlement ?? null,
        isActive: typeof details.isActive === 'boolean' ? details.isActive : (typeof subscription.isActive === 'boolean' ? subscription.isActive : null),
        currentPeriodEnd: details.currentPeriodEnd ?? subscription.currentPeriodEnd ?? null,
        willRenew: typeof details.willRenew === 'boolean' ? details.willRenew : (typeof subscription.willRenew === 'boolean' ? subscription.willRenew : null),
        graceState: details.graceState ?? subscription.graceState ?? 'none',
        serverTime: details.serverTime ?? subscription.serverTime ?? null,
        startDate: details.startDate ?? subscription.startDate ?? null,
        endDate: details.endDate ?? subscription.endDate ?? null
    };
};

export const UserProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(false);
    const [initialized, setInitialized] = useState(false);

    const normalizeUserData = (userData, previousUser = null) => {
        if (!userData) return null;

        const rawSubscription = userData.subscription ?? previousUser?.subscription ?? {
            id: 0,
            status: 'Free',
            startDate: new Date().toISOString(),
            endDate: null
        };
        const subscription = {
            ...rawSubscription,
            status: normalizeSubscriptionStatus(rawSubscription.status),
            entitlement: rawSubscription.entitlement ?? previousUser?.subscription?.entitlement ?? null,
            isActive: typeof rawSubscription.isActive === 'boolean'
                ? rawSubscription.isActive
                : (typeof previousUser?.subscription?.isActive === 'boolean' ? previousUser.subscription.isActive : null),
            currentPeriodEnd: rawSubscription.currentPeriodEnd ?? previousUser?.subscription?.currentPeriodEnd ?? null,
            willRenew: typeof rawSubscription.willRenew === 'boolean'
                ? rawSubscription.willRenew
                : (typeof previousUser?.subscription?.willRenew === 'boolean' ? previousUser.subscription.willRenew : null),
            graceState: rawSubscription.graceState ?? previousUser?.subscription?.graceState ?? 'none',
            serverTime: rawSubscription.serverTime ?? previousUser?.subscription?.serverTime ?? null,
            plan: rawSubscription.plan ?? previousUser?.subscription?.plan ?? null,
            isAnnual: typeof rawSubscription.isAnnual === 'boolean'
                ? rawSubscription.isAnnual
                : (typeof previousUser?.subscription?.isAnnual === 'boolean' ? previousUser.subscription.isAnnual : false)
        };

        const id = userData.userId ?? userData.id ?? previousUser?.userId ?? previousUser?.id;
        return {
            id: id,  // Alias for convenience
            isAdmin: userData.isAdmin ?? previousUser?.isAdmin ?? false,
            clubAdminCount: userData.clubAdminCount ?? previousUser?.clubAdminCount ?? 0,
            userId: id,
            userName: userData.userName ?? userData.username ?? previousUser?.userName,
            email: userData.email ?? previousUser?.email,
            mobileNumber: userData.mobileNumber ?? previousUser?.mobileNumber,
            firstName: userData.firstName ?? previousUser?.firstName,
            lastName: userData.lastName ?? previousUser?.lastName,
            verified: userData.verified ?? previousUser?.verified,
            profilePictureUrl: userData.profilePictureUrl ?? previousUser?.profilePictureUrl,
            dateOfBirth: userData.dateOfBirth ?? previousUser?.dateOfBirth,
            notifyByEmail: userData.notifyByEmail ?? previousUser?.notifyByEmail ?? false,
            notifyBySMS: userData.notifyBySMS ?? previousUser?.notifyBySMS ?? false,
            notifyByCall: userData.notifyByCall ?? previousUser?.notifyByCall ?? false,
            shareShootingScores: userData.shareShootingScores ?? previousUser?.shareShootingScores ?? false,
            shareLastShoots: userData.shareLastShoots ?? previousUser?.shareLastShoots ?? false,
            shareClubMemberships: userData.shareClubMemberships ?? previousUser?.shareClubMemberships ?? false,
            shareEquipmentDetails: userData.shareEquipmentDetails ?? previousUser?.shareEquipmentDetails ?? false,
            publicProfileVisibility: userData.publicProfileVisibility ?? previousUser?.publicProfileVisibility ?? 'private',
            subscription: subscription
        };
    };

    const updateUser = useCallback((userData) => {
        setUser((previousUser) => normalizeUserData(userData, previousUser));
        setInitialized(true);
    }, []);

    const fetchUserProfile = useCallback(async (forceRefresh = false) => {
        if (loading) return user;
        if (initialized && !forceRefresh) return user;

        setLoading(true);
        try {
            const userData = await accountService.getCurrentUser();
            let normalizedUser = normalizeUserData(userData, user);

            if (normalizedUser?.userId) {
                try {
                    const subscriptionDetails = await subscriptionService.getStatus();
                    normalizedUser = {
                        ...normalizedUser,
                        subscription: mergeSubscriptionDetails(normalizedUser.subscription, subscriptionDetails)
                    };
                } catch (subscriptionError) {
                    console.debug('Subscription status refresh skipped:', subscriptionError?.message || subscriptionError);
                }
            }

            setUser(normalizedUser);
            setInitialized(true);
            return normalizedUser;
        } catch (error) {
            if (error instanceof ApiError && error.status === 401) {
                setUser(null);
                setInitialized(true);
                return null;
            }
            console.error('Error fetching user profile:', error);
            setUser(null);
            setInitialized(true);
            return null;
        } finally {
            setLoading(false);
        }
    }, [initialized, loading, user]);

    useEffect(() => {
        if (!initialized && !loading) {
            fetchUserProfile();
        }
    }, [fetchUserProfile, initialized, loading]);

    const logout = useCallback(async () => {
        try {
            await accountService.logout();
        } finally {
            setUser(null);
            setInitialized(false);
        }
    }, []);

    return (
        <UserContext.Provider value={{
            user,
            setUser: updateUser,
            fetchUserProfile,
            logout,
            isAuthenticated: !!user?.userId,
            loading,
            initialized
        }}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => useContext(UserContext);
